import {ref} from 'vue'
import {deleteVideo, fetchVideo, FindVideoDocument, postVideo} from '/@src/api/contentRepo'
import {ContentStatusValue, MediaFile, RawArticle, RawVideo, VideoEpisode} from "/@src/models/content";
import useApi, {useApollo} from "/@src/composable/useApi";
import {Exact, Scalars, Tag, TagRelation, Video} from "/@src/models/graphApi";
import {ContentKind, ContentStatus} from "/@src/models/api";
import useContentBase from "/@src/composable/content/useContentBase";
import {VideoForm} from "/@src/models/form";
import {getQiniuFileInfo} from "/@src/api/mediaRepo";
import {UploadStatus, VideoFile} from "/@src/models/upload";
import _ from "lodash";
import {mapperContentStatusValue} from "/@src/utils/contentUtils";
import useTagRelations from "/@src/composable/content/useTagRelations";

export type FindVideoQueryVariables = Exact<{
  id: Scalars['Int'];
}>;
export type FindVideoQuery = { __typename?: 'query', video?: { __typename?: 'Video', id?: number | null | undefined, title?: string | null | undefined, cover_url?: string | null | undefined, status?: string | null | undefined, description?: string | null | undefined, display_time?: any | null | undefined, planned_published_at?: any | null | undefined, vip_visibility?: number | null | undefined, related_article_ids?: Array<number | null | undefined> | null | undefined, related_report_ids?: Array<number | null | undefined> | null | undefined, author?: { __typename?: 'Author', id?: number | null | undefined, nickname?: string | null | undefined } | null | undefined, related_articles?: Array<{ __typename?: 'Article', id?: number | null | undefined, title?: string | null | undefined, display_time?: any | null | undefined, author?: { __typename?: 'Author', id?: number | null | undefined, nickname?: string | null | undefined } | null | undefined } | null | undefined> | null | undefined, related_reports?: Array<{ __typename?: 'Report', id?: number | null | undefined, title?: string | null | undefined, display_time?: any | null | undefined } | null | undefined> | null | undefined, counter?: { __typename?: 'Counter', viewed?: number | null | undefined } | null | undefined, tags?: Array<{ __typename?: 'Tag', id?: number | null | undefined, name?: string | null | undefined, cover_url?: string | null | undefined } | null | undefined> | null | undefined, tag_relations?: Array<{ __typename?: 'TagRelation', id?: number | null | undefined, group?: number | null | undefined } | null | undefined> | null | undefined, episodes?: Array<{ __typename?: 'VideoEpisode', id?: number | null | undefined, name?: string | null | undefined, duration?: number | null | undefined, url?: string | null | undefined, preview_url?: string | null | undefined, thumbnail_urls?: Array<string | null | undefined> | null | undefined, size?: number | null | undefined } | null | undefined> | null | undefined } | null | undefined };
export default function useVideo() {
  const api = useApi()
  const apollo = useApollo()
  const {updateContentForm} = useContentBase()
  const {updateTagsWithRelations,setupTagRelations} = useTagRelations()
  // @ts-ignore
  const videoForm = ref<VideoForm>({
    isFeatured:false,
    isSchedulePublish:false,
    relatedArticles:[],
    relatedReports:[]
  })
  videoForm.value.content = {
  }
  const refreshThumbnail = async (id:number) => {
    const ret = await fetchVideo(api,id)
    if(ret.sets&&ret.sets.length>0&&ret.sets[0].thumbnails&&ret.sets[0].thumbnails.length>0){
      for(let index=0;index<ret.sets.length;index++){
        const episode = ret.sets[index]
        if(episode.thumbnails&&episode.thumbnails.length>0){
          // 新建视频时, 内部视频sets未空
          if(!videoForm.value.content.episodes||videoForm.value.content.episodes.length===0){
            videoForm.value.content.episodes = []
            videoForm.value.content.episodes[index] = {
              id:episode.id,
              name:episode.name!!,
              qiniu_url:episode.qiniu_url,
              preview_qiniu_url:episode.preview_qiniu_url,
              key:episode.key,
              type:'video/mp4'
            }
          }
          videoForm.value.content.episodes[index]!!.thumbnail_urls = []
          episode.thumbnails.forEach(it=>{
            videoForm.value.content.episodes!![index]!!.thumbnail_urls!!.push(it.qiniu_url)
          })
          //将thumbnail赋值给video file
          if(videoForm.value.videoFiles&&videoForm.value.videoFiles.length>0){
            const videoFile = videoForm.value.videoFiles&&videoForm.value.videoFiles[index]
            if(videoFile.url === episode.qiniu_url){
              videoFile.thumbnails = videoForm.value.content.episodes[index]!!.thumbnail_urls
            }
          }
        }
      }
    }
    return ret
  }
  const getVideo = async (variables: FindVideoQueryVariables) => {
    const ret = await apollo.query<FindVideoQuery, FindVideoQueryVariables>({query:FindVideoDocument,variables:variables})
    const {__typename, ...content} = _.cloneDeep(ret.data.video) as Video
    videoForm.value = {
      id:ret.data.video!!.id,
      content:content,
      tags:content.tags as Tag[],
      tagRelations:content.tag_relations as TagRelation[],
      isSchedulePublish:content.planned_published_at?true:false,
      displayTime:content.display_time?new Date(content.display_time):undefined,
      scheduleTime:content.planned_published_at?new Date(content.planned_published_at):undefined,
      relatedArticles:content.related_articles as RawArticle[],
      relatedReports:content.related_reports as MediaFile[],
    }
    // 转为小写状态值
    videoForm.value!!.content.status = videoForm.value.content.status?.toLowerCase()
    if(content.episodes&&content.episodes.length>0){
      videoForm.value!!.videoFiles = []
      content.episodes.forEach(it=>{
        const file:VideoFile = {
          id:it!!.id,
          name:it!!.name!!,
          url:it!!.url!!,
          previewUrl:it!!.preview_url,
          uploadStatus:UploadStatus.SUCCESS,
          size:it!!.size
        }
        if(it!!.thumbnail_urls &&it!!.thumbnail_urls.length>0){
          file.thumbnails = []
          it!!.thumbnail_urls.forEach(it=>{
            file.thumbnails!!.push(it!)
          })
        }
        videoForm.value!.videoFiles!.push(file)
      })
    }
    updateContentForm(videoForm.value!!)
    videoForm.value.tags = updateTagsWithRelations(videoForm.value.tagRelations!,videoForm.value.tags)
    return ret
  }
  /**
   * 将内容format成老的视频类型
   */
  const mapperRawContent = async ():Promise<RawVideo>=>{
    const data:RawVideo = {
      title:videoForm.value!!.content.title!!,
      id:videoForm.value.content.id!!,
      vip_visibility:videoForm.value.content.vip_visibility!!,
      description:videoForm.value.content.description,
      // duration:videoResponse.value.duration,
      tag_relations:videoForm.value.tagRelations,
      display_time:videoForm.value.content.display_time?.toISOString(),
      cover:videoForm.value.content.cover_url,
      // page_views:videoResponse.value.viewCount!!
      // sets:videoResponse.value.episodes
    }
    if(videoForm.value.freeExpireAt){
      data.free_expired_at = videoForm.value.freeExpireAt.toISOString()
    }else {
      data.free_expired_at = new Date(1000).toISOString()
    }
    if(videoForm.value.isSchedulePublish&&videoForm.value.scheduleTime){
      data.schedule={
        open:true,
        t:videoForm.value.scheduleTime?.toISOString()
      }
    }
    // 设置关联内容
    if(videoForm.value.relatedArticles && videoForm.value.relatedArticles.length>0){
      const ids:Array<number> = []
      for(const article of videoForm.value.relatedArticles){
        ids.push(article.id!!)
      }
      data.related_article_ids = ids
    }
    if(videoForm.value.relatedReports && videoForm.value.relatedReports.length>0){
      const ids:Array<number> = []
      for(const report of videoForm.value.relatedReports){
        ids.push(report.id!!)
      }
      data.related_research_ids = ids
    }
    // 后台创建时间和显示时间相同，总是取创建时间来显示
    data.created_at = data.display_time!!
    if(videoForm.value.videoFiles&&videoForm.value.videoFiles.length>0){
      data.sets = []
      for(let index = 0;index<videoForm.value.videoFiles.length;index++){
        const videoFile = videoForm.value.videoFiles[index]
        const episode:VideoEpisode = {
          id:videoFile.id,
          name:videoFile.name!!,
          qiniu_url:videoFile.url,
          preview_qiniu_url:videoFile.previewUrl,
          key:videoFile.uploadKey,
          type:'video/mp4',
        }
        //设置key,便于后台通过key生成缩略图
        episode.key = episode.qiniu_url?.substring(episode.qiniu_url?.lastIndexOf('/')+1)
        if(!videoFile.duration||videoFile.duration===0){
          if(episode.qiniu_url){
            const ret = await getQiniuFileInfo(api,episode.qiniu_url!!)
            episode.duration = Math.round(parseFloat(ret.format.duration))
            episode.size = parseInt(ret.format.size)
          }
        }
        if(episode.preview_qiniu_url){
          episode.preview_ok = true
        }
        if(videoFile.thumbnails&&videoFile.thumbnails.length>0){
          episode.thumbnail_ok = true
          episode.thumbnails = []
          videoFile.thumbnails.forEach(it=>{
            episode.thumbnails?.push({
              qiniu_url:it
            })
          })
        }
        data.sets.push(episode)
      }
    }
    data.status = mapperContentStatusValue(videoForm.value!.content.status as ContentStatus)
    return data
  }
  const postVideoContent = async (form:VideoForm) => {
    // 目前视频总是原创
    form.isOriginal = true
    setupTagRelations(form,ContentKind.VIDEO)
    const data = await mapperRawContent()
    return postVideo(api,data)
  }
  /**
   * 删除视频
   * @param id
   */
  const deleteVideoContent = (id:number)=>{
    const promise =  deleteVideo(api,id)
    videoForm.value.content.status = ContentStatus.DELETED
    return promise
  }
  /**
   * 保存视频
   * @param form
   */
  const saveVideoContent = async (form:VideoForm) => {
    // 目前视频总是原创
    form.isOriginal = true
    setupTagRelations(form,ContentKind.VIDEO)
    // 生成原始视频对象
    const data = await mapperRawContent()
    data.status = ContentStatusValue.DRAFT
    const video = await postVideo(api,data)
    form.id = video.id
    form.content.id = video.id
    // form.status = ContentStatus.DRAFT
    // 更新视频文件列表
  }
  return {
    videoForm,
    getVideo,
    // mapperGqlVideo,
    postVideoContent,
    saveVideoContent,
    refreshThumbnail,
    deleteVideoContent
  }
}
